import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuditsApi } from '@buspro/web/apis';
import { TripAuditReportComponent, saveDownloadedFile } from '@buspro/web/shared';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NGXLogger } from 'ngx-logger';
import { lastValueFrom } from 'rxjs';

/**
 * Admin wrapper around the shared {@link TripAuditReportComponent}: reads `:operationId`, fetches
 * the report via the admin `AuditsApi`, owns loading/error state, and routes Back to My Audits.
 *
 * Export PDF downloads the server-rendered report — the same file POS and the tenant web hand
 * out, with who audited it shown to Local Administrators and Local Managers.
 */
@Component({
  selector: 'buspro-admin-trip-audit-report-page',
  standalone: true,
  imports: [CommonModule, NzButtonModule, NzIconModule, NzSpinModule, TripAuditReportComponent],
  templateUrl: './trip-audit-report-page.component.html',
})
export class TripAuditReportPageComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private auditsApi = inject(AuditsApi);
  private logger = inject(NGXLogger);
  private message = inject(NzMessageService);

  operationId = signal<string | null>(this.route.snapshot.paramMap.get('operationId'));

  reportQuery = injectQuery(() => ({
    queryKey: ['trip-audit-report', this.operationId()],
    enabled: !!this.operationId(),
    queryFn: () => {
      const id = this.operationId();
      if (!id) throw new Error('Operation id is missing');
      return lastValueFrom(this.auditsApi.auditsControllerGetTripAuditReport({ operationId: id }));
    },
    onError: (error: unknown) => {
      this.logger.error('Failed to load trip audit report:', error);
    },
  }));

  report = computed(() => this.reportQuery.data() ?? null);

  exportingPdf = signal(false);

  async exportPdf() {
    const id = this.operationId();
    if (!id || this.exportingPdf()) return;
    this.exportingPdf.set(true);
    try {
      const response = await lastValueFrom(
        this.auditsApi.auditsControllerGetTripAuditReportPdf$Response({ operationId: id }),
      );
      saveDownloadedFile(response, `trip-report-${id.slice(0, 8)}.pdf`);
    } catch (error) {
      this.logger.error('Failed to download the trip report PDF:', error);
      this.message.error('The report PDF could not be downloaded. Please try again.');
    } finally {
      this.exportingPdf.set(false);
    }
  }

  back() {
    this.router.navigate(['/audits/my-audits']);
  }

  retry() {
    this.reportQuery.refetch();
  }
}
